import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import {Button} from '../inputs/button';
import {CheckCircleIcon, CrossIcon, InfoCircleIcon, WarningTriangleIcon} from '../svg-icon';
import {cls} from '../../utils';

import css from './toast.module.css';

export type ToastIntent = 'info' | 'success' | 'warning' | 'error';

export type ToastOptions = {
	title: React.ReactNode;
	/** текст под заголовком */
	body?: React.ReactNode;
	intent?: ToastIntent;
	/** через сколько миллисекунд тост скрывается сам */
	timeout?: number;
};

type ToastEntry = ToastOptions & {id: number};

type ToastContextValue = {
	show(options: ToastOptions): void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_TIMEOUT = 4000;
const MAX_TOASTS = 4;

const INTENT_ICONS: Record<ToastIntent, React.ReactNode> = {
	info: <InfoCircleIcon />,
	success: <CheckCircleIcon />,
	warning: <WarningTriangleIcon />,
	error: <WarningTriangleIcon />,
};

export const ToastProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
	const [toasts, setToasts] = useState<ToastEntry[]>([]);
	const nextId = useRef(0);

	const dismiss = useCallback((id: number) => setToasts((current) => current.filter((toast) => toast.id !== id)), []);
	const show = useCallback((options: ToastOptions) => {
		const id = nextId.current++;
		setToasts((current) => [...current, {...options, id}].slice(-MAX_TOASTS));
	}, []);
	const value = useMemo(() => ({show}), [show]);

	return (
		<ToastContext.Provider value={value}>
			{children}
			<div className={css.toaster} role="status" aria-live="polite">
				{toasts.map((toast) => (
					<Toast key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
				))}
			</div>
		</ToastContext.Provider>
	);
};

const Toast: React.FC<{toast: ToastEntry; onDismiss(): void}> = ({toast, onDismiss}) => {
	const {title, body, intent = 'info', timeout = DEFAULT_TIMEOUT} = toast;
	const [isPaused, setPaused] = useState(false);
	const dismissRef = useRef(onDismiss);
	dismissRef.current = onDismiss;

	useEffect(() => {
		if (isPaused) return;
		const timer = setTimeout(() => dismissRef.current(), timeout);
		return () => clearTimeout(timer);
	}, [isPaused, timeout]);

	return (
		<div
			className={cls(css.toast, css[intent])}
			onMouseEnter={() => setPaused(true)}
			onMouseLeave={() => setPaused(false)}
		>
			<span className={css.icon}>{INTENT_ICONS[intent]}</span>
			<div className={css.content}>
				<div className={css.title}>{title}</div>
				{body && <div className={css.body}>{body}</div>}
			</div>
			<Button
				className={css.dismiss}
				appearance="transparent"
				size="small"
				icon={<CrossIcon />}
				aria-label="Закрыть"
				onClick={onDismiss}
			/>
		</div>
	);
};

export function useToast(): ToastContextValue {
	const context = useContext(ToastContext);
	if (!context) throw new Error('useToast must be inside ToastProvider');
	return context;
}
